import { useRef } from "react";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";

/**
 * CHAPTER SCENE — gepinnte Kapitel-Zwischenszene zwischen den Akten.
 *
 * Riesige Outline-Nummer fährt beim Hereinkommen aus der Tiefe heran,
 * Kicker/Label steigen scrub-gesteuert aus Masken auf, goldene Linie wächst.
 * Die Bühne pinnt für 80vh Scroll-Strecke; der Exit (Auflösen) übernimmt die
 * äußere Scene. Transparent — die WebGL-Partikelwelt morpht darunter mit.
 * Alles reversibel. Reduced-Motion: statische, sichtbare Variante.
 */
export default function ChapterScene({ nr, label, testId }) {
  const wrapRef = useRef(null);
  const reduced = useReducedMotion();

  // Einfahrt: Wrapper zieht über die vorherige Szene
  const { scrollYProgress: enterP } = useScroll({
    target: wrapRef,
    offset: ["start end", "start start"],
  });
  // Pin: 80vh Scrub-Strecke, Bühne klebt
  const { scrollYProgress: pinP } = useScroll({
    target: wrapRef,
    offset: ["start start", "end end"],
  });

  const numScale = useTransform(enterP, [0, 1], [1.7, 1]);
  const numOpacity = useTransform(enterP, [0, 0.6], [0, 1]);
  const kickerY = useTransform(pinP, [0.05, 0.35], ["110%", "0%"]);
  const labelY = useTransform(pinP, [0.15, 0.5], ["110%", "0%"]);
  const lineScale = useTransform(pinP, [0.3, 0.65], [0, 1]);
  const drift = useTransform(pinP, [0, 1], [30, -30]);

  if (reduced) {
    return (
      <section
        data-testid={testId}
        className="relative py-28 flex flex-col items-center justify-center text-center"
      >
        <span
          aria-hidden="true"
          className="font-display leading-none select-none"
          style={{
            fontSize: "clamp(120px, 26vw, 400px)",
            color: "transparent",
            WebkitTextStroke: "1px rgba(212,175,55,0.35)",
          }}
        >
          {nr}
        </span>
        <span className="font-display text-xs uppercase tracking-[0.35em] text-gold mb-3">
          Kapitel
        </span>
        <span className="font-display uppercase text-5xl md:text-7xl text-paper">{label}</span>
        <div className="mt-6 h-px bg-gold w-24 mx-auto" />
      </section>
    );
  }

  return (
    <div ref={wrapRef} data-testid={testId} className="relative h-[180vh]">
      {/* Gepinnte Bühne */}
      <div
        className="sticky top-0 h-screen flex items-center justify-center overflow-hidden"
        style={{ perspective: "1200px" }}
      >
        {/* Riesige Outline-Nummer im Hintergrund */}
        <motion.div
          aria-hidden="true"
          className="absolute inset-0 flex items-center justify-center pointer-events-none select-none"
          style={{ scale: numScale, opacity: numOpacity, y: drift }}
        >
          <span
            className="font-display leading-none"
            style={{
              fontSize: "clamp(100px, 34vw, 520px)",
              color: "transparent",
              WebkitTextStroke: "1.5px rgba(212,175,55,0.35)",
              lineHeight: 1,
            }}
          >
            {nr}
          </span>
        </motion.div>

        {/* Kicker + Label + goldene Linie */}
        <div className="relative z-10 flex flex-col items-center text-center px-6 gap-4">
          <div className="overflow-hidden">
            <motion.span
              className="block font-display text-xs uppercase tracking-[0.35em] text-gold"
              style={{ y: kickerY }}
            >
              Kapitel
            </motion.span>
          </div>
          <div className="overflow-hidden">
            <motion.h2
              className="block font-display uppercase text-5xl md:text-7xl text-paper"
              style={{ y: labelY }}
            >
              {label}
            </motion.h2>
          </div>
          <motion.div
            className="h-px bg-gold origin-left"
            style={{ scaleX: lineScale, width: "clamp(60px, 10vw, 120px)" }}
          />
        </div>
      </div>
    </div>
  );
}
